import type { AppError } from "@/shared/api";

/**
 * Канал показу photo-помилки (spec §8):
 * - `inline` — у `FileInput :error` (примітив має `role=alert` error-слот);
 * - `notFound` — bubble наверх (host-панель «майстра вже немає»): 404
 *   `master_not_found` / 400 `invalid_id` (defensive, host уже валідував id);
 * - `toast` — generic foreground toast (403 / неочікуване).
 */
export type PhotoErrorSurface = "inline" | "notFound" | "toast";

/**
 * Типобезпечні i18n-ключі photo-помилок (`t()`). Доменні `masters.photo.errors.*`
 * поверх generic `errors.*` (ADR-0010 Follow-up).
 */
export type PhotoErrorKey =
  | "masters.photo.errors.unsupportedType"
  | "masters.photo.errors.invalidImage"
  | "masters.photo.errors.tooLarge"
  | "masters.photo.errors.uploadFailed"
  | "masters.photo.errors.masterGone"
  | "errors.forbidden"
  | "errors.network"
  | "errors.server"
  | "errors.unknown";

export interface MappedPhotoError {
  surface: PhotoErrorSurface;
  i18nKey: PhotoErrorKey;
}

/**
 * Чиста `AppError → {surface, i18nKey}` (spec §8, S13). Мапінг по **`code`**, бо
 * 413/415 колапсують у `kind=server` (ADR-0010 status→kind) — лише `code` їх
 * розрізняє. Без router/store/toast/i18n — застосовує викликач (composable).
 * Інваріант S6: фото оновлюється лише на 200; будь-яка помилка лишає попереднє.
 * Cancel (`isCancel`) сюди не доходить — composable ловить його прапором `canceled`.
 */
export function mapPhotoError(e: AppError): MappedPhotoError {
  switch (e.kind) {
    case "validation":
      // 422 invalid_image → нечитабельне/завелике за розмірами (inline).
      if (e.code === "invalid_image") {
        return { surface: "inline", i18nKey: "masters.photo.errors.invalidImage" };
      }
      // 400 invalid_id → майстра немає (bubble → host-панель).
      if (e.code === "invalid_id") {
        return { surface: "notFound", i18nKey: "masters.photo.errors.masterGone" };
      }
      // 400 invalid_body (missing photo part) / решта validation — generic inline
      // (FileInput гарантує part → це баг/дрейф контракту).
      return { surface: "inline", i18nKey: "masters.photo.errors.uploadFailed" };
    case "notFound":
      // 404 master_not_found на upload/delete → майстра вже немає (bubble).
      return { surface: "notFound", i18nKey: "masters.photo.errors.masterGone" };
    case "server":
      // 413/415/500 (ADR-0010 status→kind) → розрізняємо по code.
      if (e.code === "unsupported_image_type") {
        return { surface: "inline", i18nKey: "masters.photo.errors.unsupportedType" };
      }
      if (e.code === "payload_too_large") {
        return { surface: "inline", i18nKey: "masters.photo.errors.tooLarge" };
      }
      // 500 / 413 без code (дрейф) → generic server (inline, retriable).
      return { surface: "inline", i18nKey: "errors.server" };
    case "network":
      return { surface: "inline", i18nKey: "errors.network" };
    case "forbidden":
      return { surface: "toast", i18nKey: "errors.forbidden" };
    default:
      // auth (401 ловить глобальний interceptor) / unknown / rateLimited / conflict.
      return { surface: "toast", i18nKey: "errors.unknown" };
  }
}
